import type {
  AdminNotificationRequest,
  NotificationChannel,
  NotificationSeverity,
  UserListItem,
  UsersListResponse
} from '@/types';

import React from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Autocomplete,
  AutocompleteItem,
  Chip,
  Select,
  SelectItem,
  Switch,
  Textarea
} from '@heroui/react';
import { useAtom } from 'jotai';
import { RiCloseLine, RiDeleteBinLine, RiNotificationLine } from '@remixicon/react';
import { toast } from 'react-toastify';

import { sendAdminNotification } from '@/store';
import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';
import { ErrorHandler } from '@/utils/errorHandler';

type MinimalUser = Pick<UserListItem, 'id' | 'first_name' | 'last_name' | 'email'>;

type MetadataEntry = {
  id: string;
  key: string;
  value: string;
};

interface UserNotificationModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  targetUser: MinimalUser | null;
  showRecipientSelector?: boolean;
  onSendSuccess?: () => void;
}

const SEVERITY_OPTIONS: Array<{ key: NotificationSeverity; label: string }> = [
  { key: 'info', label: 'Info' },
  { key: 'success', label: 'Success' },
  { key: 'warning', label: 'Warning' },
  { key: 'error', label: 'Error' }
];

const DEFAULT_CHANNELS: NotificationChannel[] = ['in_app'];

const generateMetadataId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random()}`;
};

const isValidUrl = (value: string): boolean => {
  if (!value.trim()) {
    return true;
  }

  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

function getUserLabel(user: MinimalUser): string {
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();

  return fullName ? `${fullName} (${user.email})` : user.email;
}

export function UserNotificationModal({
  isOpen,
  onOpenChange,
  targetUser,
  showRecipientSelector = true,
  onSendSuccess
}: UserNotificationModalProps) {
  const [, sendNotification] = useAtom(sendAdminNotification);

  const [selectedUsers, setSelectedUsers] = React.useState<MinimalUser[]>([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<MinimalUser[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);

  const [channels, setChannels] = React.useState<NotificationChannel[]>(DEFAULT_CHANNELS);
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [severity, setSeverity] = React.useState<NotificationSeverity | undefined>(undefined);
  const [actionUrl, setActionUrl] = React.useState('');
  const [metadataEntries, setMetadataEntries] = React.useState<MetadataEntry[]>([]);

  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const primaryRecipient = selectedUsers[0];

  const resetForm = React.useCallback(() => {
    setSelectedUsers(targetUser ? [targetUser] : []);
    setSearchTerm('');
    setSearchResults([]);
    setChannels(DEFAULT_CHANNELS);
    setTitle('');
    setMessage('');
    setSeverity(undefined);
    setActionUrl('');
    setMetadataEntries([]);
    setFieldErrors({});
  }, [targetUser]);

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    } else {
      setSearchTerm('');
      setSearchResults([]);
    }
  }, [isOpen, resetForm]);

  const fetchUsers = React.useCallback(
    async (term: string) => {
      if (!term.trim()) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const { data } = await apiClient.get('/users', {
          params: buildApiParams({ per_page: 10 }, { search: term.trim() })
        });

        const response = data as UsersListResponse;
        setSearchResults(response.data ?? []);
      } catch (error) {
        ErrorHandler.handle(error);
      } finally {
        setIsSearching(false);
      }
    },
    []
  );

  React.useEffect(() => {
    if (!isOpen) return;

    const handler = window.setTimeout(() => {
      void fetchUsers(searchTerm);
    }, 300);

    return () => {
      window.clearTimeout(handler);
    };
  }, [searchTerm, fetchUsers, isOpen]);

  const handleUserAdd = React.useCallback(
    (userId: string | null) => {
      if (!userId) return;

      const user =
        selectedUsers.find((u) => u.id.toString() === userId) ||
        searchResults.find((u) => u.id.toString() === userId);

      if (!user) return;

      setSelectedUsers((prev) => {
        if (prev.some((u) => u.id === user.id)) {
          return prev;
        }
        return [...prev, user];
      });
      setSearchTerm('');
      setFieldErrors((prev) => {
        const { user_ids, ...rest } = prev;
        return rest;
      });
    },
    [selectedUsers, searchResults]
  );

  const handleUserRemove = React.useCallback((userId: number) => {
    setSelectedUsers((prev) => prev.filter((user) => user.id !== userId));
  }, []);

  const toggleChannel = React.useCallback((channel: NotificationChannel) => {
    setChannels((prev) => {
      const hasChannel = prev.includes(channel);
      const next = hasChannel ? prev.filter((c) => c !== channel) : [...prev, channel];

      setFieldErrors((prevErrors) => {
        if (next.length > 0 && prevErrors.channels) {
          const { channels: _channels, ...rest } = prevErrors;
          return rest;
        }

        if (next.length === 0) {
          return { ...prevErrors, channels: 'Select at least one channel' };
        }

        return prevErrors;
      });

      return next;
    });
  }, []);

  const addMetadataEntry = React.useCallback(() => {
    setMetadataEntries((prev) => [
      ...prev,
      {
        id: generateMetadataId(),
        key: '',
        value: ''
      }
    ]);
  }, []);

  const updateMetadataEntry = React.useCallback(
    (id: string, field: 'key' | 'value', value: string) => {
      setMetadataEntries((prev) =>
        prev.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry))
      );
    },
    []
  );

  const removeMetadataEntry = React.useCallback((id: string) => {
    setMetadataEntries((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const buildMetadataObject = React.useCallback(() => {
    return metadataEntries.reduce<Record<string, string>>((acc, entry) => {
      const key = entry.key.trim();
      if (!key) {
        return acc;
      }

      acc[key] = entry.value;
      return acc;
    }, {});
  }, [metadataEntries]);

  const validateForm = React.useCallback(() => {
    const errors: Record<string, string> = {};

    if (selectedUsers.length === 0) {
      errors.user_ids = 'Select at least one user';
    }

    if (channels.length === 0) {
      errors.channels = 'Select at least one channel';
    }

    if (!title.trim()) {
      errors.title = 'Title is required';
    } else if (title.trim().length > 255) {
      errors.title = 'Title must be at most 255 characters';
    }

    if (!message.trim()) {
      errors.message = 'Message is required';
    }

    if (actionUrl.trim() && !isValidUrl(actionUrl.trim())) {
      errors.action_url = 'Enter a valid URL';
    }

    setFieldErrors(errors);

    return errors;
  }, [selectedUsers, channels, title, message, actionUrl]);

  const handleSubmit = React.useCallback(async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const payload: AdminNotificationRequest = {
      user_ids: selectedUsers.map((user) => user.id),
      channels,
      title: title.trim(),
      message: message.trim(),
      severity,
      action_url: actionUrl.trim() || undefined,
      metadata: undefined
    };

    const metadata = buildMetadataObject();
    if (Object.keys(metadata).length > 0) {
      payload.metadata = metadata;
    }

    setIsSubmitting(true);
    try {
      const response = await sendNotification(payload);
      const successMessage = response?.meta?.message ?? 'Notification sent successfully';
      toast.success(successMessage);
      onSendSuccess?.();
      resetForm();
      onOpenChange(false);
    } catch (error) {
      const apiValidationErrors = ErrorHandler.handle(error);
      if (apiValidationErrors.length > 0) {
        const mappedErrors: Record<string, string> = {};
        apiValidationErrors.forEach(({ field, message: errorMessage }) => {
          mappedErrors[field] = errorMessage;
        });
        setFieldErrors(mappedErrors);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateForm,
    selectedUsers,
    channels,
    title,
    message,
    severity,
    actionUrl,
    buildMetadataObject,
    sendNotification,
    onSendSuccess,
    resetForm,
    onOpenChange
  ]);

  const handleClose = React.useCallback(
    (open: boolean) => {
      if (!open) {
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  return (
    <Modal isOpen={isOpen} placement="center" size="lg" onOpenChange={handleClose} isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex items-center gap-2">
              <RiNotificationLine className="h-5 w-5 text-primary" />
              <span>Send Notification</span>
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-6">
                {showRecipientSelector ? (
                <div className="flex flex-col gap-4">
                  <Autocomplete
                    label="Add recipients"
                    placeholder="Search users by name or email"
                    inputValue={searchTerm}
                    onInputChange={setSearchTerm}
                    onSelectionChange={(key) => handleUserAdd(key as string | null)}
                    isLoading={isSearching}
                  >
                    {searchResults.map((user) => (
                      <AutocompleteItem key={user.id.toString()} textValue={getUserLabel(user)}>
                        <div className="flex flex-col">
                          <span className="font-medium">{getUserLabel(user)}</span>
                          <span className="text-small text-default-500">{user.email}</span>
                        </div>
                      </AutocompleteItem>
                    ))}
                  </Autocomplete>
                  {fieldErrors.user_ids && (
                    <span className="text-danger text-xs">{fieldErrors.user_ids}</span>
                  )}

                  {selectedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedUsers.map((user) => (
                        <Chip
                          key={user.id}
                          color="primary"
                          variant="flat"
                          endContent={
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              className="h-5 w-5 min-w-5"
                              onPress={() => handleUserRemove(user.id)}
                            >
                              <RiCloseLine className="h-3 w-3" />
                            </Button>
                          }
                        >
                          {getUserLabel(user)}
                        </Chip>
                      ))}
                    </div>
                  )}
                </div>
                ) : (
                  <div className="rounded-lg border border-default-200 bg-default-50 p-4">
                    <div className="text-sm font-medium text-default-600">Recipient</div>
                    <div className="text-sm text-default-700">
                      {primaryRecipient ? getUserLabel(primaryRecipient) : 'No recipient selected'}
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <span className="text-sm font-medium">Channels</span>
                  <div className="flex flex-wrap items-center gap-4">
                    <Switch
                      isSelected={channels.includes('in_app')}
                      onValueChange={() => toggleChannel('in_app')}
                    >
                      In-app
                    </Switch>
                    <Switch
                      isSelected={channels.includes('email')}
                      onValueChange={() => toggleChannel('email')}
                    >
                      Email
                    </Switch>
                  </div>
                  {fieldErrors.channels && (
                    <span className="text-danger text-xs">{fieldErrors.channels}</span>
                  )}
                </div>

                <Input
                  label="Title"
                  placeholder="Enter notification title"
                  value={title}
                  onValueChange={(value) => {
                    setTitle(value);
                    setFieldErrors((prev) => {
                      if (!prev.title) {
                        return prev;
                      }
                      const { title: _title, ...rest } = prev;
                      return rest;
                    });
                  }}
                  isInvalid={Boolean(fieldErrors.title)}
                  errorMessage={fieldErrors.title}
                  isRequired
                />

                <Textarea
                  label="Message"
                  placeholder="Write the notification message"
                  value={message}
                  onValueChange={(value) => {
                    setMessage(value);
                    setFieldErrors((prev) => {
                      if (!prev.message) {
                        return prev;
                      }
                      const { message: _message, ...rest } = prev;
                      return rest;
                    });
                  }}
                  isInvalid={Boolean(fieldErrors.message)}
                  errorMessage={fieldErrors.message}
                  minRows={4}
                  isRequired
                />

                <Select
                  label="Severity (optional)"
                  placeholder="Select severity"
                  selectedKeys={severity ? new Set([severity]) : new Set()}
                  onSelectionChange={(keys) => {
                    const key = Array.from(keys as Set<string>)[0] as NotificationSeverity | undefined;
                    setSeverity(key);
                  }}
                >
                  {SEVERITY_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>

                <Input
                  label="Action URL (optional)"
                  placeholder="https://example.com"
                  value={actionUrl}
                  onValueChange={(value) => {
                    setActionUrl(value);
                    setFieldErrors((prev) => {
                      if (!prev.action_url) {
                        return prev;
                      }
                      const { action_url: _actionUrl, ...rest } = prev;
                      return rest;
                    });
                  }}
                  isInvalid={Boolean(fieldErrors.action_url)}
                  errorMessage={fieldErrors.action_url}
                />

                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Metadata (optional)</span>
                    <Button size="sm" variant="light" onPress={addMetadataEntry}>
                      Add field
                    </Button>
                  </div>

                  {metadataEntries.length === 0 ? (
                    <span className="text-xs text-default-500">
                      No metadata added. Use &quot;Add field&quot; to include custom key/value pairs.
                    </span>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {metadataEntries.map((entry) => (
                        <div key={entry.id} className="flex items-end gap-3">
                          <Input
                            label="Key"
                            placeholder="domain_id"
                            value={entry.key}
                            onValueChange={(value) => updateMetadataEntry(entry.id, 'key', value)}
                            className="flex-1"
                          />
                          <Input
                            label="Value"
                            placeholder="42"
                            value={entry.value}
                            onValueChange={(value) => updateMetadataEntry(entry.id, 'value', value)}
                            className="flex-1"
                          />
                          <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            color="danger"
                            onPress={() => removeMetadataEntry(entry.id)}
                          >
                            <RiDeleteBinLine className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>
                Cancel
              </Button>
              <Button color="primary" isLoading={isSubmitting} onPress={handleSubmit}>
                Send Notification
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}


